import { useEffect, useState, useCallback, useRef } from 'react';
import './CitationConnector.css';

interface CitationConnectorProps {
  citationElement: HTMLElement | null;
  panelElement: HTMLElement | null;
  isClosing?: boolean;
  onOutOfView?: () => void;
  panelOnLeft?: boolean;
}

interface ConnectorPoints {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

export function CitationConnector({ citationElement, panelElement, isClosing, onOutOfView, panelOnLeft }: CitationConnectorProps) {
  const [points, setPoints] = useState<ConnectorPoints | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  // Use ref for wasVisible to avoid race conditions with async state updates
  const wasVisibleRef = useRef(false);
  // Track mount time to add stabilization delay
  const mountTimeRef = useRef(Date.now());

  // Reset wasVisible synchronously when citation element changes
  useEffect(() => {
    wasVisibleRef.current = false;
    mountTimeRef.current = Date.now();
  }, [citationElement]);

  const calculatePoints = useCallback((): ConnectorPoints | null => {
    if (!citationElement || !panelElement) return null;

    const citationRect = citationElement.getBoundingClientRect();
    const panelRect = panelElement.getBoundingClientRect();

    // Start from the appropriate side of the citation
    const startX = panelOnLeft
      ? citationRect.left - 4  // Connect from left of citation to left panel
      : citationRect.right + 4; // Connect from right of citation to right panel
    const startY = citationRect.top + citationRect.height / 2;

    // End at the appropriate edge of the panel
    const panelVisibleTop = Math.max(panelRect.top, 0);
    const panelVisibleBottom = Math.min(panelRect.bottom, window.innerHeight);
    const panelVisibleCenterY = (panelVisibleTop + panelVisibleBottom) / 2;

    const endX = panelOnLeft
      ? panelRect.right + 4  // Connect to right edge of left panel
      : panelRect.left - 4;  // Connect to left edge of right panel
    const endY = panelVisibleCenterY;

    return { startX, startY, endX, endY };
  }, [citationElement, panelElement, panelOnLeft]);

  const updatePoints = useCallback(() => {
    // Skip all logic on mobile - connector doesn't render and shouldn't trigger closes
    const isMobile = window.innerWidth < 768;
    if (isMobile) return;

    const newPoints = calculatePoints();
    setPoints(newPoints);

    // Only show if citation is in view
    if (newPoints && citationElement) {
      const citationRect = citationElement.getBoundingClientRect();
      const inView = citationRect.top > -50 && citationRect.bottom < window.innerHeight + 50;
      setIsVisible(inView);

      // Track if connector was ever visible, then became invisible
      if (inView) {
        wasVisibleRef.current = true;
      } else if (wasVisibleRef.current && !inView && onOutOfView) {
        // Add stabilization delay - don't trigger close within 500ms of mount
        // This prevents false triggers during initial render/positioning
        if (Date.now() - mountTimeRef.current > 500) {
          // Citation scrolled out of view - close the panel
          onOutOfView();
        }
      }
    }
  }, [calculatePoints, citationElement, onOutOfView]);

  useEffect(() => {
    if (!citationElement || !panelElement) return;

    // Initial calculation
    updatePoints();

    // Update on scroll with requestAnimationFrame for smoothness
    let rafId: number;
    const handleScroll = () => {
      rafId = requestAnimationFrame(updatePoints);
    };

    // Update on resize
    const handleResize = () => {
      rafId = requestAnimationFrame(updatePoints);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    // Also listen for scroll on any scrollable parent
    let scrollParent: HTMLElement | null = citationElement.parentElement;
    while (scrollParent) {
      scrollParent.addEventListener('scroll', handleScroll, { passive: true });
      scrollParent = scrollParent.parentElement;
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);

      let parent: HTMLElement | null = citationElement.parentElement;
      while (parent) {
        parent.removeEventListener('scroll', handleScroll);
        parent = parent.parentElement;
      }
    };
  }, [citationElement, panelElement, updatePoints]);

  // Don't render on mobile or if no points
  if (!points || window.innerWidth < 768) return null;

  const { startX, startY, endX, endY } = points;

  // Create a gentle S-curve with bezier control points
  const controlPoint1X = startX + (endX - startX) * 0.3;
  const controlPoint1Y = startY;
  const controlPoint2X = endX - (endX - startX) * 0.3;
  const controlPoint2Y = endY;

  // Path: move to start, cubic bezier to end
  const pathD = `M ${startX} ${startY} C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${endX} ${endY}`;

  return (
    <svg
      className={`citation-connector ${isClosing ? 'citation-connector--closing' : ''} ${isVisible ? 'citation-connector--visible' : ''}`}
      aria-hidden="true"
    >
      {/* Glow effect */}
      <defs>
        <filter id="connector-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Main curved line */}
      <path
        d={pathD}
        className="citation-connector__line"
        filter="url(#connector-glow)"
      />

      {/* Start dot at citation */}
      <circle
        cx={startX}
        cy={startY}
        r="3"
        className="citation-connector__dot"
      />

      {/* End dot at panel */}
      <circle
        cx={endX}
        cy={endY}
        r="3"
        className="citation-connector__dot"
      />
    </svg>
  );
}
